/**
 * Cart Image Preload
 * Preloads cart images in the background for faster cart drawer loading
 */

(function() {
  'use strict';

  let preloadTimeout = null;
  const preloadedImages = new Set();

  // Preload cart images in the background
  async function preloadCartImages() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.item_count === 0) return;
      
      // Group items by product_id (same logic as cart drawer render)
      const grouped = {};
      cart.items.forEach((item) => {
        if (!grouped[item.product_id]) {
          grouped[item.product_id] = {
            image: item.image
          };
        }
      });
      
      // Preload images for each product
      Object.values(grouped).forEach((product) => {
        if (product.image) {
          // Use same image size as cart drawer (200x200)
          const imageUrl = product.image.replace(/(\.[^.]+)$/, '_200x$1');
          
          // Skip if already preloaded
          if (preloadedImages.has(imageUrl)) return;
          preloadedImages.add(imageUrl);
          
          // Preload using Image API
          const img = new Image();
          img.src = imageUrl;
          
          // Also add link preload for better browser optimization
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = imageUrl;
          document.head.appendChild(link);
        }
      });
    } catch (error) {
      // Silently fail - preloading is optional
      console.debug('Cart image preload failed:', error);
    }
  }

  // Debounced preload function
  function debouncedPreload() {
    if (preloadTimeout) {
      clearTimeout(preloadTimeout);
    }
    preloadTimeout = setTimeout(preloadCartImages, 300);
  }

  // Intercept fetch requests to cart endpoints
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0].url;
    
    // If it's a cart-related request, preload images after a delay
    if (url && (url.includes('/cart/add') || url.includes('/cart/change'))) {
      const fetchPromise = originalFetch.apply(this, args);
      
      // Preload images after cart operations complete
      fetchPromise.then(() => {
        debouncedPreload();
      }).catch(() => {
        // Still try to preload even on error
        debouncedPreload();
      });
      
      return fetchPromise;
    }
    
    return originalFetch.apply(this, args);
  };

  // Expose function globally for Cart class to use
  window.preloadCartImages = preloadCartImages;

  // Preload on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadCartImages);
  } else {
    preloadCartImages();
  }

  // Also preload after cart updates (listen for custom events)
  document.addEventListener('cart:updated', debouncedPreload);
  document.addEventListener('cart:item-added', debouncedPreload);
})();
